import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import autobind from 'class-autobind';
import throttle from 'lodash/throttle';
import isEqual from 'lodash/isEqual';
import omitBy from 'lodash/omitBy';
import isFunction from 'lodash/isFunction';
import includes from 'lodash/includes';

import Button from 'zoomforth_component_lib/btn_basic.jsx';
import VideoishPlayer from 'zoomforth_component_lib/videoish_player.jsx';
import loadFittedImage from 'zoomforth_zfutils/load_fitted_image.js';
import { canViewPDF } from 'zoomforth_zfutils/browser_pdf_support.js';
import Presentation from 'zoomforth_presentation/presentation_root.jsx';
import { getPreviewCollaborationSettings } from 'zoomforth_codesbane_viewer/reducers/index.js';
import { 
  getPdfIconUrl, 
  getPptIconUrl, 
  getOtherAssetIconUrl,
  isVideoishAssetType
} from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';

const IFRAMED_ITEM_TYPES = ['ppt', 'pdf', 'html_package', 'iframe_embed', 'video', 'youtube'];

/*************************************************************************************************/
/*** PROPS ***************************************************************************************/
/*************************************************************************************************/

const PROP_TYPES = {
  videoishPlayerProps: PropTypes.shape({
    autoplay: PropTypes.bool,
    sources: PropTypes.arrayOf(PropTypes.object),
    subtitleTrack: PropTypes.string,
    posterImageUrl: PropTypes.string,
    onEnded: PropTypes.func,
    onError: PropTypes.func,
    onPause: PropTypes.func,
    onPlay: PropTypes.func,
    onSeeked: PropTypes.func,
    onStart: PropTypes.func,
    onTimeUpdate: PropTypes.func
  }).isRequired
};

const mapStateToProps = (reduxState, props) => {
  const previewCollaborationSettings = getPreviewCollaborationSettings(reduxState);
  const isPreviewCollaborationEnabled = !!previewCollaborationSettings.isEnabled;

  const iframeWrapperProps = {};
  if(isPreviewCollaborationEnabled && includes(IFRAMED_ITEM_TYPES, props.itemType)){
    iframeWrapperProps['data-velt-iframe-container'] = true;
  }

  return {
    iframeWrapperProps
  };
};

const mapDispatchToProps = () => { return {}; };

/*************************************************************************************************/
/*** LEGACY **************************************************************************************/
/*************************************************************************************************/

class OverlayAssetViewport extends Component {
    
  constructor(props) {
    super(props);
    autobind(this);
  }

  componentDidMount() {

    this.setUpContentItem();
    
    $(window).on('resize.zoomforth-overlay-asset', throttle(()=> {
      this.setImagePositionIfImageAsset();
    }, 50));
  }

  componentDidUpdate() {
    // TODO: depending on how we implement the editor,
    // we probably want to run this less frequently than on any update
    this.setImagePositionIfImageAsset();
  }

  componentWillUnmount() {
    $(window).off('resize.zoomforth-overlay-asset');
    
    if (this.embeddedVideo) {
      this.embeddedVideo.remove();
      this.embeddedVideo = null; 
    }
  }

  shouldComponentUpdate(nextProps) {
    // Only re-render when a non-function prop has changed
    // WARNING: This means this component must only accept simple props (no objects!)
    return !isEqual(omitBy(this.props, isFunction), omitBy(nextProps, isFunction));
  }

  setImagePositionIfImageAsset() {
    const itemType = this.props.itemType;
    if (itemType == 'photo') {
      // TODO: Replace this with FittedImageContainer component
      return loadFittedImage({
        container: $(this.assetViewportRef),
        imageSrc: this.props.assetDisplayImage,
        altText: this.props.altText,
        imageClassName: 'overlay-asset'
      });
    }
  }


  setUpContentItem() {
    
    const {
      itemType,
      shouldRenderAsMobile,
      onLoadingChange
    } = this.props;

    if (itemType === 'photo') {
      this.setImagePositionIfImageAsset().done(()=> {
        onLoadingChange({ loading:false });
      });

    } else if (itemType === 'video' || itemType === 'youtube') {
      onLoadingChange({ loading:false });
    
    } else if (itemType === 'pdf' || itemType === 'other' || itemType === 'html_package' || itemType === 'iframe_embed') {
      // I don't think we expect to see "other" items show up in an overlay, 
      // but it is technically possible, so we cover the use case.
      // I think the behavior the page has is to just download the file right away, and not be shown in an overlay.
      // our old code handled this case, though, so the new case might as well. technically you can navigate to "other" items in the overlay
      onLoadingChange({ loading:false });
      
    } else if (itemType === 'ppt') {
      if (shouldRenderAsMobile) {
        onLoadingChange({ loading:false });
      }
    }
  }

  handleAssetViewportClick(e) {
    // let it keep propagating, thus closing the overlay, if 
    // we clicked on the overlay itself and not one if its children
    if (!$(e.target).hasClass('overlay-asset-viewport')){
      e.stopPropagation();
    }
  }

  handleRef(ref) {
    this.assetViewportRef = ref;
  }

  render() {
    const {
      slides,
      assetLink,
      assetDownloadLink,
      assetExtension,   
      shouldRenderAsMobile,
      htmlPackageEntrypointUrl,
      iframeEmbedUrl,
      iframeTitle,
      itemType,
      pptIsLoading,
      displayFilename,
      downloadLink,
      canDownloadAsset,
      renderedTileCaption,
      videoishPlayerProps,
      iframeWrapperProps,
      onLoadingChange,
      onPresentationSlideDisplay,
      onPresentationDownloadClick
    } = this.props;
    
    return (
      <div 
        className={classNames('overlay-asset-viewport', {
          'show-overflow': shouldRenderAsMobile,
          'ppt-loading': pptIsLoading
        })}
        ref={this.handleRef}
        onClick={this.handleAssetViewportClick}
        {...iframeWrapperProps}
      >
        {
          // images get their content added dynamically in setUpContentItem, otherwise:

          isVideoishAssetType(itemType) ?

            <VideoishPlayer {...videoishPlayerProps} />

          : itemType === 'pdf' && (shouldRenderAsMobile || !canViewPDF()) ?

            <div className="pdf-overlay-notice">
              <div className="downloadable-asset-wrapper"
                onClick={()=>{
                  if (canDownloadAsset) {
                    window.open(assetLink, '_blank');
                  }
                }}
              >
                <img src={getPdfIconUrl()} />
                <div
                  className="downloadable-asset-title" 
                  dangerouslySetInnerHTML={{
                    __html: (
                      canDownloadAsset ?
                        renderedTileCaption + '.pdf'
                      :
                        'You cannot view this on a mobile device. Please open on desktop.'
                    )
                  }}
                />
                {
                  canDownloadAsset &&
                  <Button className="fyi-text">
                    {
                      shouldRenderAsMobile ?
                        'Tap to open PDF'
                      : 
                        'PDF cannot be displayed inline because no viewer could be found. Click to download.'
                    }
                  </Button>
                }
              </div>
            </div>

          : itemType === 'pdf' ?

              <iframe className="pdf-iframe" 
                src={assetLink}
                title={iframeTitle || 'PDF Viewer'}
                frameBorder="0" 
                allowFullScreen="true" 
              />

          : itemType === 'ppt' && shouldRenderAsMobile ?
            
            <div className="ppt-overlay-notice">
              <div className="downloadable-asset-wrapper"
                onClick={()=>{
                  if (canDownloadAsset) {
                    window.open(assetDownloadLink, '_blank');
                  }
                }}  
              >
                <img className="ppt-image" src={getPptIconUrl()}/>
                <div className="downloadable-asset-title" 
                  dangerouslySetInnerHTML={{
                    __html: (
                      canDownloadAsset ?
                        renderedTileCaption + '.ppt'
                      :
                        'You cannot view this on a mobile device. Please open on desktop.'
                    )
                  }}
                />
                {
                  canDownloadAsset &&
                  <Button className="fyi-text">
                    Tap to view Presentation
                  </Button>
                }
              </div>
            </div>

          : itemType === 'ppt' ?
            
            <Presentation
              slides={slides}
              displayFilename={displayFilename}
              downloadLink={downloadLink}
              canDownloadAsset={canDownloadAsset}
              focusOnMount={true}
              onSlideDisplay={onPresentationSlideDisplay}
              onDownloadClick={onPresentationDownloadClick}
              onPresentationLoad={() => {
                onLoadingChange({
                  loading: false,
                  pptIsLoading: false
                });
              }}
            />

          : itemType === 'html_package' ?
            
            <iframe className="html-package-iframe" 
              src={htmlPackageEntrypointUrl}
              title={iframeTitle || 'HTML Package Viewer'}
              frameBorder="0" 
              allowFullScreen="true" 
            />

          : itemType === 'iframe_embed' ?
            
            <iframe className="custom-embed-iframe"
              src={iframeEmbedUrl}
              title={iframeTitle || 'Custom Embed Viewer'}
              frameBorder="0" 
              allowFullScreen="true" 
            />
            
          : itemType === 'other' ?

              <div className="other-overlay-notice" onClick={()=>{
                window.open(assetLink, '_blank');
              }}>
                <div className="downloadable-asset-wrapper">
                  <img src={getOtherAssetIconUrl()}/>
                  <div className="downloadable-asset-title" 
                    dangerouslySetInnerHTML={
                      {__html: renderedTileCaption + '.' + assetExtension}
                    }
                  />
                  <Button className="fyi-text">
                    Click to download file
                  </Button>
                </div>
              </div>
              
          :  null
        }
      </div>
    );
  }
}

OverlayAssetViewport.propTypes = PROP_TYPES;

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OverlayAssetViewport);
